/**
 * 원장 PDF 등록 화면의 순수 로직(DOM 없음). 정정 입력 해석과 요청 본문 조립만 한다.
 * 정답·보기를 추측하지 않는다: 해석할 수 없는 입력은 오류로 돌려주고 서버 미리보기가 다시 판정한다.
 */
export interface PdfFiles { readonly questionPdf: string; readonly answerPdf: string; readonly explanationPdf: string }
export interface CorrectionDraft { readonly section: string; readonly number: number; options?: string[]; accepted?: number[]; explanation?: string; semantics?: 'any_one_accepted' | 'all_required' }

export const SET_ID = /^[A-Za-z0-9_-]{1,40}$/;

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** "1, 3" → [1,3]. 비었거나 숫자가 아니거나 범위(1..optionCount) 밖이면 null */
export function parseAccepted(text: string, optionCount: number): number[] | null {
  const parts = text.split(/[,\s]+/).filter((x) => x !== '');
  if (parts.length === 0) return null;
  const nums = parts.map((x) => (/^[0-9]{1,2}$/.test(x) ? Number(x) : NaN));
  if (nums.some((n) => !Number.isInteger(n) || n < 1 || n > optionCount) || new Set(nums).size !== nums.length) return null;
  return nums.sort((a, b) => a - b);
}

export const parseOptions = (text: string): string[] => text.split('\n').map((x) => x.trim()).filter((x) => x !== '');

/** 같은 구역·번호의 정정은 마지막 입력으로 합친다(이전 필드는 유지) */
export function mergeCorrection(list: readonly CorrectionDraft[], next: CorrectionDraft): CorrectionDraft[] {
  const rest = list.filter((c) => !(c.section === next.section && c.number === next.number));
  const prev = list.find((c) => c.section === next.section && c.number === next.number);
  return [...rest, { ...prev, ...next }];
}

export type MultiSemantics = 'any_one_accepted' | 'all_required';
/** bulk: 지점별 정정이 없는 복수 정답 전체에 적용할, 사람이 명시한 뜻. 없으면 서버가 뜻 미확정으로 막는다 */
export function previewBody(files: PdfFiles, corrections: readonly CorrectionDraft[], bulk?: MultiSemantics): Record<string, unknown> {
  return { ...files, ...(corrections.length > 0 ? { corrections } : {}), ...(bulk !== undefined ? { multiAnswerSemantics: bulk } : {}) };
}

/** setId·releaseTitle은 비워 두면 서버가 내용 기준으로 정한다(수동 입력 최소화). 교사는 classId(담당 반)가 필요하다 */
export interface RegisterInput { setId: string; releaseTitle: string; confirmed: boolean; classId?: string; multiAnswerSemantics?: MultiSemantics; confirmAnswerKey?: boolean; autoEvidence?: boolean; evidencePicks?: readonly EvidencePick[] }

/** 교사가 고른 근거 유형(선택). 확인 필요 지점 중 유형을 고를 수 있는 지점에만 허용된다 */
export interface EvidencePick { section: string; number: number; category: string }
export interface EvidenceInfo { sentence: string | null; options: string[]; accepted: number[]; explanation: string | null; previews: Record<string, { stem: string; options: string[]; correctNo: number }> | null }
export interface EvidenceException { section: string; number: number; reason: string | null; pickable?: boolean; info?: EvidenceInfo | null }

/** 복수 정답의 뜻만 비어 있는 지점(정답은 정상 인식됨). 이 지점들은 공통 규칙 하나로 한꺼번에 정하고, 다른 문제가 있는 지점만 실제 확인이 필요하다 */
export const SEMANTICS_ONLY = 'answer_semantics_unconfirmed';
export function isSemanticsOnly(issues: readonly string[]): boolean { return issues.length > 0 && issues.every((c) => c === SEMANTICS_ONLY); }
export function countIssueKinds(sections: readonly { points: readonly { issues: readonly string[] }[] }[]): { semanticsOnly: number; real: number } {
  let semanticsOnly = 0; let real = 0;
  for (const s of sections) for (const p of s.points) { if (p.issues.length === 0) continue; if (isSemanticsOnly(p.issues)) semanticsOnly += 1; else real += 1; }
  return { semanticsOnly, real };
}
/** 문장 속 【대상 표현】을 강조하기 위해 조각으로 나눈다 */
export function splitTarget(sentence: string): { text: string; target: boolean }[] {
  return sentence.split(/(【[^】]*】)/).filter((x) => x !== '').map((x) => (x.startsWith('【') && x.endsWith('】') ? { text: x.slice(1, -1), target: true } : { text: x, target: false }));
}
const EVIDENCE_REASON: Record<string, string> = {
  explanation_missing: '해설이 없어요', explanation_format: '해설이 "정답 — 근거 → 결론" 꼴이 아니에요', answer_not_in_explanation: '해설 머리말에 정답 글자가 없어요',
  multiple_accepted_answers: '인정 정답이 여러 개라 근거를 하나로 정할 수 없어요', no_grammar_term: '해설에 문법 용어가 없어 유형을 고를 수 없어요',
  lexical_and_grammar_mixed: '어휘와 문법 설명이 섞여 있어요',
  verifier_hold: '자동 검사가 문장·해설·보기의 일관성을 확인하지 못해 근거 문제를 만들지 않았어요(보류). 교사가 직접 확인해야 해요',
  verifier_caveat: '자동 검사에서 확인하지 못한 조건이 남아 있어 근거 문제를 만들지 않았어요(보류)',
  no_reason_rule:'해설의 조건→이유를 문장으로 옮길 규칙이 없어 근거 문제를 만들지 않았어요(보류)',
};
export const evidenceReasonText = (code: string | null): string => (code === null ? '이유 없음' : EVIDENCE_REASON[code] ?? `확인이 필요해요(${code})`);
/** 예외 목록을 이유별로 묶는다(같은 이유는 한 번에 보고 한 번에 '근거 없이 등록'으로 처리). 이유 순서는 처음 나온 순 */
export function groupExceptions(list: readonly EvidenceException[]): { reason: string | null; items: EvidenceException[] }[] {
  const out: { reason: string | null; items: EvidenceException[] }[] = [];
  for (const x of list) {
    const g = out.find((y) => y.reason === x.reason);
    if (g === undefined) out.push({ reason: x.reason, items: [x] }); else g.items.push(x);
  }
  return out;
}
/** 등록 본문. 같은 내용이면 항상 같은 본문이 되어 서버가 새로 만들지 않는다 */
export function registerBody(files: PdfFiles, corrections: readonly CorrectionDraft[], r: RegisterInput): Record<string, unknown> {
  return {
    ...previewBody(files, corrections, r.multiAnswerSemantics), contentVersion: 'v1', confirmed: r.confirmed,
    ...(r.setId !== '' ? { setId: r.setId } : {}), ...(r.releaseTitle !== '' ? { releaseTitle: r.releaseTitle } : {}), ...(r.classId ? { classId: r.classId } : {}),
    // 등록자가 이 화면에서 한 번 확인하면 서버가 정답 기준을 확인·적용한다(원장 별도 승인 없음, 지점별 클릭 없음)
    ...(r.confirmAnswerKey === true ? { confirmAnswerKey: true } : {}),
    // 해설 기반 근거 문제 후보도 같은 확인으로 적재·확인(교사 직접 입력 없음). 후보 없는 지점은 '근거 자료 없음'
    ...(r.autoEvidence === true ? { autoEvidence: true } : {}),
    // 유형 선택은 근거 함께 등록을 켰을 때만 보낸다(서버도 켜지 않으면 거부). 같은 선택이면 같은 본문
    ...(r.autoEvidence === true && (r.evidencePicks?.length ?? 0) > 0 ? { evidencePicks: [...r.evidencePicks!].sort((x, y) => x.section.localeCompare(y.section) || x.number - y.number) } : {}),
  };
}

export type Role = 'teacher' | 'director';
export function registerBlocker(r: RegisterInput, blockingCount: number, role: Role = 'director'): string | null {
  if (blockingCount > 0) return '막는 문제가 남아 있어 등록할 수 없어요. 표시된 항목을 고치거나 PDF를 확인해 주세요.';
  if (role === 'teacher' && !r.classId) return '등록할 담당 반을 선택해 주세요.';
  if (r.setId !== '' && !SET_ID.test(r.setId)) return '자료 ID는 비워 두거나 영문·숫자·-·_ 40자 이내로 입력해 주세요.';
  if (r.releaseTitle.length > 150) return '자료 제목은 150자 이내로 입력해 주세요.';
  if (!r.confirmed) return '원문과 대조했다는 확인에 체크해 주세요.';
  return null;
}

const ISSUE: Record<string, string> = {
  duplicate_number: '번호가 중복돼요', too_few_options: '보기가 2개 미만이에요', option_duplicate: '같은 보기가 반복돼요', number_sequence: '번호가 1부터 이어지지 않아요',
  no_sections: '구역을 찾지 못했어요', section_missing: '이 구역이 정답지·해설지에 없어요', section_not_in_question: '문제지에 없는 구역이에요', section_duplicate: '구역 이름이 중복돼요',
  answer_semantics_unconfirmed: '정답은 정상으로 읽혔어요. 인정 정답이 여러 개라서 "하나만 골라도 정답"인지 "모두 골라야 정답"인지만 정하면 돼요(위의 공통 규칙으로 한 번에 정할 수 있어요)',
  selection_mode_unsupported: '"모두 골라야 정답"은 인정 정답이 2개 이상인 지점에만 지정할 수 있어요',
  explanation_missing: '해설이 없어요', answer_missing: '정답을 찾지 못했어요',
  number_not_in_question: '문제지에 없는 번호예요', correction_target_missing: '정정 대상을 찾지 못했어요',
};
export const issueText = (code: string): string => ISSUE[code] ?? `확인이 필요해요(${code})`;

export function failMessage(status: number, body: Record<string, any>): string {
  const e = String(body['error'] ?? '');
  if (e === 'pdf_needs_ocr') return String(body['message'] ?? '스캔 PDF는 지원하지 않아요. 텍스트 PDF를 올려 주세요.');
  if (e === 'pdf_unreadable') return `PDF를 읽지 못했어요(${String(body['field'] ?? '')}: ${String(body['reason'] ?? '')}).`;
  if (e === 'pdf_too_large') return 'PDF가 너무 커요(파일당 4MB 이내).';
  if (status === 403) return '이 계정은 할 수 없는 작업이에요.';
  if (status === 401) return '다시 로그인해 주세요.';
  return `처리하지 못했어요(${e || status}).`;
}
